import { getEnumKeyFromNum } from '@pai/commons'

import { LogLevel } from '../log-level.enum'

/**
 * Intl.DateTimeFormat instance for formatting time as HH:MM:SS.sss
 */
export const timeFormat = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  fractionalSecondDigits: 3,
  hour12: false,
})
type JsonStringifyReplacer = (key: string, value: unknown) => unknown

export interface ErrorAttributes {
  [key: string]: unknown

  name: string
  location: string
  message: string
  stack?: string
  cause?: unknown
}

/**
 * Format an error into a loggable object.
 *
 * @example
 * ```json
 * {
 *   "name": "Error",
 *   "location": "file.js:1",
 *   "message": "An error occurred",
 *   "stack": "Error: An error occurred\n    at file.js:1\n    at file.js:2\n    at file.js:3",
 *   "cause": {
 *     "name": "OtherError",
 *     "location": "file.js:2",
 *     "message": "Another error occurred",
 *     "stack": "Error: Another error occurred\n    at file.js:2\n    at file.js:3\n    at file.js:4"
 *   }
 * }
 * ```
 *
 * @param error - Error to format
 */
export function formatError(error: Error): ErrorAttributes {
  const { name, message, stack, cause, ...errorAttributes } = error
  const formattedError: ErrorAttributes = {
    name,
    location: getCodeLocation(error.stack),
    message,
    stack,
    cause: cause instanceof Error ? formatError(cause) : cause,
  }
  for (const key in error) {
    if (typeof key === 'string' && !['name', 'message', 'stack', 'cause'].includes(key)) {
      formattedError[key] = (errorAttributes as Record<string, unknown>)[key]
    }
  }

  return formattedError
}

/**
 * Get the location of an error from a stack trace.
 *
 * @param stack - stack trace to parse
 */
function getCodeLocation(stack?: string): string {
  if (!stack) {
    return ''
  }

  const stackLines = stack.split('\n')
  const regex = /\(([^()]*?):(\d+?):(\d+?)\)\\?$/

  for (const item of stackLines) {
    const match = regex.exec(item)

    if (Array.isArray(match)) {
      return `${match[1]}:${Number(match[2])}`
    }
  }

  return ''
}

/**
 * handles circular references & bigints and formats errors
 * @param jsonStringifyReplacer - custom replacer function which is called prior to the built-in handling
 */
export function getJsonStringifyReplacer(jsonStringifyReplacer?: JsonStringifyReplacer): JsonStringifyReplacer {
  const references = new WeakSet<object>()

  return (key, value) => {
    let replacedValue = jsonStringifyReplacer ? jsonStringifyReplacer(key, value) : value

    if (typeof replacedValue === 'bigint') {
      return replacedValue.toString()
    } else if (replacedValue instanceof Error) {
      replacedValue = formatError(replacedValue)
    }

    if (typeof replacedValue === 'object' && replacedValue !== null) {
      if (references.has(replacedValue)) {
        return `<circular reference>`
      } else {
        references.add(replacedValue)
      }
    }

    return replacedValue
  }
} // used to enforce the usage of the factory function without any runtime overhead

/**
 * make sure to use the {@link createJsonLogObject} util function to create an instance of this interface
 */
export interface JsonLogObject {
  level: string
  time: string /* ISO string */
  logger: string

  message?: string
  error?: ErrorAttributes

  data?: unknown
}

export function createJsonLogObject(
  level: LogLevel,
  clazzName: string,
  timestamp: Date,
  args: unknown[],
): JsonLogObject {
  const logObjectData: Partial<JsonLogObject> = {
    level: getEnumKeyFromNum(LogLevel, level),
    time: timestamp.toISOString(),
    logger: clazzName,
  }

  // if first arg is string, it's the message
  if (typeof args[0] === 'string') {
    logObjectData.message = args.shift() as string
  }

  // if any arg is Error, extract it (only first one)
  // --> if no specific message, use error message as log message
  const errIndex = args.findIndex((arg) => arg instanceof Error)
  if (errIndex !== -1) {
    logObjectData.error = formatError(args.splice(errIndex, 1)[0] as Error)
    if (!logObjectData.message) {
      logObjectData.message = logObjectData.error.message
    }
  }

  // remaining args go to data
  if (args.length) {
    logObjectData.data = args.length === 1 ? args[0] : args
  }

  return logObjectData as JsonLogObject
}
